import { useSearchParams } from 'react-router-dom';

import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import { readSelectionsFromSearchParams } from '@/components/filters/ProductSelectionsUrl';
import type { ProductHierarchySelections } from '@/components/filters/ProductSelectionsUrl';
import { useTenant } from '@/providers/TenantProvider';

export type SkuRecord = {
  productId: string;
  productDescription: string;
};

export interface ProductSkus {
  error: Error | null;
  isFetching: boolean;
  isLoading: boolean;
  page: number;
  pageSize: number;
  skus: SkuRecord[];
  totalCount: number;
}

export type SkuSortKey = keyof SkuRecord;
export type SortDir = 'asc' | 'desc';

type Props = {
  page?: number;
  pageSize?: number;
  query?: string;
  selections?: ProductHierarchySelections;
  sortKey?: SkuSortKey;
  sortDir?: SortDir;
};

type CatalogProductsApiResponse = {
  items?: { product_id?: string; product_description?: string }[];
  limit?: number;
  offset?: number;
  total?: number;
};

// API will throw an error if "limit" > 100
const MAX_CATALOG_PRODUCTS_PAGE_SIZE = 100;

export function useProductSkus({
  page = 1,
  pageSize = 25,
  query: queryFromProps,
  selections: selectionsFromProps,
  sortKey = 'productId',
  sortDir = 'asc',
}: Props): ProductSkus {
  const { tenantAlias } = useTenant();

  const [searchParams] = useSearchParams();

  const query = queryFromProps ?? searchParams.get('query') ?? '';
  const selections: ProductHierarchySelections = selectionsFromProps ?? readSelectionsFromSearchParams(searchParams);
  const safePageSize = Math.min(pageSize, MAX_CATALOG_PRODUCTS_PAGE_SIZE);
  const offset = Math.max(0, (page - 1) * safePageSize);
  const requestFilters: Record<string, string[]> = {};

  if (selections.businessGroup) requestFilters.business_group = [selections.businessGroup];
  if (selections.productLine) requestFilters.product_line = [selections.productLine];
  if (selections.productClass) requestFilters.product_class = [selections.productClass];

  const queryResult = useApiQuery<CatalogProductsApiResponse>(
    tenantAlias,
    ApiEndpoint.CatalogProducts,
    {
      query: {
        offset,
        limit: safePageSize,
        include_total: true,
        include_attrs: true,
        query,
        filters: JSON.stringify(requestFilters),
      },
    },
    {
      placeholderData: (prev) => prev,
    },
  );

  const data = queryResult?.data ?? {};
  const { items = [], limit, total = 0 } = data;
  const skus: SkuRecord[] = items.map((item) => ({
    productId: item.product_id ?? '',
    productDescription: item.product_description ?? '',
  }));
  const sortedSkus = [...skus].sort((a, b) => {
    const aValue = a[sortKey] ?? '';
    const bValue = b[sortKey] ?? '';
    const comparison = aValue.localeCompare(bValue);
    return sortDir === 'desc' ? -comparison : comparison;
  });

  return {
    error: queryResult.error,
    isFetching: queryResult.isFetching,
    isLoading: queryResult.isLoading,
    page,
    pageSize: limit ?? safePageSize,
    skus: sortedSkus,
    totalCount: total,
  };
}
